import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReportCard from "component/reports/ReportCard";
import Transport from "../../assets/images/report_img.jpg";
import { reportService } from "../../services/reportService";
import AiGenerateReportField from "component/reports/AiGenerateReportField";

const mapCustomReport = (r = {}) => ({
  id: r.id,
  title: r.title || r.prompt || "",
  summary: r.ai_summary || "",
  image_url: r.hero_image_url || Transport,
  match_rate: (() => {
    const num = Number(r.match_score ?? r.match_rate ?? r.score);
    if (!Number.isFinite(num)) return null;
    return Math.round(num <= 1 ? num * 100 : num);
  })(),
  sectors: [],
  recent_at: r.created_at || r.updated_at,
  reading_time_minutes: r.reading_time_minutes || r.read_time_minutes || r.reading_minutes || r?.metadata_json?.read_time_minutes || null,
  status: r.status,
  progress: (r.metadata_json && r.metadata_json.progress) || (r.status === "completed" ? 100 : 10),
  eta_hint: r?.metadata_json?.eta_hint || "2-5 minutes",
  remaining_seconds: r?.metadata_json?.remaining_seconds,
  error: r.error || "",
});

const ReportLibrary = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const navigate = useNavigate();

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportService.listCustomReports();
      const arr = Array.isArray(data) ? data.map(mapCustomReport) : [];
      setReports(arr);
    } catch (e) {
      setError(e?.message || t("reports.library.errorLoad"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleRetry = useCallback(
    async (reportId) => {
      if (!reportId) return;
      setRetryingId(reportId);
      try {
        await reportService.retryCustomReport(reportId);
        await loadReports();
      } catch (e) {
        setError(e?.message || t("reports.library.errorLoad"));
      } finally {
        setRetryingId(null);
      }
    },
    [loadReports, t]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      loadReports();
    }, 15000);
    return () => clearInterval(interval);
  }, [loadReports]);

  const handleCreated = (report) => {
    if (report) {
      setReports((prev) => {
        const mapped = mapCustomReport(report);
        const existing = prev.find((r) => r.id === mapped.id);
        if (existing) {
          return prev.map((r) => (r.id === mapped.id ? mapped : r));
        }
        return [mapped, ...prev];
      });
    }
    // Always refresh to get latest progress/status from server
    loadReports();
  };

  return (
    <div className="main__content__box pt-0">
      <div className="container-xxl ms-0">
        <div className="content_wrap">
          <div className="py-4">
            <AiGenerateReportField onCreated={handleCreated} />
          </div>
        </div>

        <div className="content_wrap">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="text-white fw-semibold mb-0">{t("reports.myReports")}</h4>
            <button type="button" className="btn btn-outline-light btn-sm px-3" onClick={() => navigate(-1)}>
              {t("common.back")}
            </button>
          </div>

          {loading && <div className="text-muted mb-3">{t("reports.library.loading")}</div>}
          {error && <div className="text-danger mb-3">{error}</div>}
          {!loading && !error && reports.length === 0 && (
            <div className="text-muted">{t("reports.library.empty")}</div>
          )}

          <div className="row g-4">
            {reports.map((report, index) => {
              const generating = report.status === "generating";
              const failed = report.status === "failed";
              const progress = report.progress || 0;
              const remaining =
                Number.isFinite(Number(report.remaining_seconds)) && Number(report.remaining_seconds) > 0
                  ? `~${Math.ceil(Number(report.remaining_seconds) / 60)} min left`
                  : report.eta_hint;
              return (
                <div className="col-md-6 col-lg-4" key={report.id || index}>
                  <div className="position-relative reportCard_wrapper">
                    <ReportCard
                      value={index + 1}
                      title={report.title || t("reports.customReport")}
                      summary={
                        report.summary ||
                        (failed ? t("reports.customReportGenerationFailed", "Generation failed") : t("reports.customReportGenerating"))
                      }
                      _id={report.status === "completed" ? report.id : null}
                      reportType="custom"
                      progress={generating ? progress : report.match_rate}
                      image_url={report.image_url}
                      sectors={report.sectors}
                      created_at={report.recent_at}
                      reading_time_minutes={report.reading_time_minutes}
                    />
                    {generating && (
                      <div className="reportCard_overlay d-flex flex-column justify-content-center align-items-center">
                        <div className="progress w-75">
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                            aria-valuenow={progress}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                        <span className="text-white small mt-2">{t("reports.generating")} ({remaining})</span>
                      </div>
                    )}
                    {failed && (
                      <div className="reportCard_overlay d-flex flex-column justify-content-center align-items-center">
                        <span className="text-danger fw-semibold text-center px-3">
                          {report.error || t("reports.customReportGenerationFailed", "Generation failed")}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm mt-2"
                          disabled={retryingId === report.id}
                          onClick={() => handleRetry(report.id)}
                        >
                          {retryingId === report.id
                            ? t("reports.retrying", "Retrying...")
                            : t("reports.retry", "Retry")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportLibrary;
